import {numericQuantity} from 'numeric-quantity';
import {numberOfTableFrameFittings} from '../../view/pages/products/configuration-constants';
import {findOne} from 'country-codes-list';
import Fraction from 'fraction.js';
import fractionUnicode from 'fraction-unicode';

export const validCountryIds = {
  dk: 'dk',
  us: 'us',
  au: 'au',
};
export const validTranslationIds = {
  da: 'da',
  en: 'en',
  se: 'se',
};
export const validUnitsIds = {
  si: 'si',
  iu: 'iu',
};

export const recordToEntries = <T extends Record<string, unknown>>(record: T, isEnum = true) => {
  return Object.keys(record)
    .filter((entry) => !isEnum || isNaN(Number(entry)))
    .map((key: keyof typeof record) => ({
      key,
      value: record[key],
    }));
};

const validCountryIdsList = recordToEntries(validCountryIds, false).map((entry) => entry.value);
const validTranslationIdsList = recordToEntries(validTranslationIds, false).map((entry) => entry.value);
const validUnitsIdsList = recordToEntries(validUnitsIds, false).map((entry) => entry.value);

export const isTranslationIdValid = (id: string) => {
  return validTranslationIdsList.some((i) => i === id);
};
export const isCountryIdValid = (id: string) => {
  let countryCode = findOne('countryCode', id.toUpperCase())?.countryCode.toLowerCase() ?? validCountryIds.dk;
  return countryCode;
};
export const isUnitsIdValid = (id: string) => {
  return validUnitsIdsList.some((i) => i === id);
};

export const getCountryCodeByName = (name: string) => {
  let countryCode = findOne('countryNameEn', name)?.countryCode.toLowerCase();
  return countryCode;
};

export const convertMmToInches = (value: string) => {
  if (value !== undefined) {
    // Round to the nearest 1/64
    var rounded = (Math.round((Number(value) / 25.4) * 64) / 64).toFixed(1);
    var f = new Fraction(rounded);
    var newVal = f.toFraction(true);
    return newVal;
  } else {
    return '';
  }
};

export const convertInchesToMm = (value: string) => {
  return (Number(value) * 25.4).toFixed(2).toString();
};
export const covertKiloToPounds = (value: string) => {
  return (Number(value) * 2.2).toFixed(0).toString();
};
export const covertPoundsToKilo = (value: string) => {
  return (Number(value) / 2.2).toFixed(0).toString();
};

export const convertMmToInchesRange = (value: string) => {
  var firstValue = value.substring(0, value.indexOf('-'));
  var secoundValue = value.substring(value.indexOf('-') + 1, value.indexOf(' '));
  return convertMmToInches(firstValue) + '-' + convertMmToInches(secoundValue) + ' inches';
};

export const stringOnlyContainsDigits = (text: string) => {
  return /^[0-9.,]+$/.test(text);
};

export const convertConfigurationLengthParams = (configurationParams: any, unitsId: string) => {
  Object.keys(configurationParams).forEach(function (key) {
    var value = numericQuantity(configurationParams[key] as string).toString();
    if (key !== numberOfTableFrameFittings && stringOnlyContainsDigits(value)) {
      if (unitsId === validUnitsIds.iu) {
        configurationParams[key] = convertInchesToMm(value);
      } else {
        configurationParams[key] = value;
      }
    }
  });
  return configurationParams;
};

export const getUnitLengthNameByCulture = (unitsId: string) => {
  let text = '';

  if (unitsId === validUnitsIds.iu) {
    text = text.concat(' inches');
  } else {
    text = text.concat(' mm');
  }

  return text;
};
export const getUnitWeightNameByCulture = (unitsId: string) => {
  if (unitsId === validUnitsIds.iu) {
    return 'lb';
  } else {
    return 'kg';
  }
};

export const getUnitLenghtByCulture = (unitsId: string, value: string) => {
  if (unitsId === validUnitsIds.iu) {
    return convertMmToInches(value);
  } else {
    return value;
  }
};

export const getUnitLengthByCultureAsText = (unitsId: string, value: string) => {
  if (unitsId !== validUnitsIds.iu) {
    return value;
  }

  var inchesValue = convertMmToInches(value);

  if (inchesValue === '') {
    return '';
  }

  const [wholePart, fractionPart] = inchesValue.split(' ');

  if (fractionPart) {
    var [numerator, denominator] = fractionPart.split('/');
    return `${wholePart} ${fractionUnicode(Number(numerator), Number(denominator))}`;
  }

  var [numeratorLessThanOne, denominatorLessThanOne] = wholePart.split('/');
  return fractionUnicode(Number(numeratorLessThanOne), Number(denominatorLessThanOne));
};
export const getUnitWeightByCulture = (unitsId: string, value: string) => {
  if (unitsId === validUnitsIds.iu) {
    return covertKiloToPounds(value);
  } else {
    return value;
  }
};
export const getUnitRangebyCulture = (unitsId: string, value: string) => {
  if (unitsId === validUnitsIds.iu) {
    return convertMmToInchesRange(value);
  } else {
    return value;
  }
};
export const getValueLengthWithUnitByCulture = (unitsId: string, value: string) => {
  return getUnitLengthByCultureAsText(unitsId, value) + getUnitLengthNameByCulture(unitsId);
};
export const getValueWeighthWithUnitByCulture = (unitsId: string, value: string) => {
  return getUnitWeightByCulture(unitsId, value) + ' ' + getUnitWeightNameByCulture(unitsId);
};

export const getValueRangeWithUnitByCulture = (unitsId: string, value1: string, value2: string) => {
  return (
    getUnitLengthByCultureAsText(unitsId, value1) +
    ' - ' +
    getUnitLengthByCultureAsText(unitsId, value2) +
    getUnitLengthNameByCulture(unitsId)
  );
};
